import React, { useState, useEffect } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import { useFetch } from './api';
import SettingsHeader from './SettingsHeader';

const MIN_LOOKBACK = 1;
const MAX_LOOKBACK = 365 * 3;

// add erp / freight entries here to expose them.
const TYPES = [
  { key: 'wms', label: 'WMS' },
  { key: 'cart', label: 'Cart' },
  { key: 'carrier', label: 'Carrier' },
];

const isInvalid = (value) => {
  // unset fields (org not yet backfilled, never edited) are not errors
  if (value == null) return false;
  return Number.isNaN(value) || value < MIN_LOOKBACK || value > MAX_LOOKBACK;
};

function ConnectionSettings() {
  const trackstar = useFetch();
  const [lookbackSettings, setLookbackSettings] = useState({});
  const [preventDuplicateInstalls, setPreventDuplicateInstalls] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updateDisabled, setUpdateDisabled] = useState(true);
  const [error, setError] = useState(null);

  const hasLookbackError = TYPES.some(({ key }) => isInvalid(lookbackSettings[key]));

  const markDirty = () => {
    setUpdateDisabled(false);
    setError(null);
  };

  const handleLookbackChange = (key, e) => {
    const value = parseInt(e.target.value, 10);
    setLookbackSettings((prev) => ({ ...prev, [key]: value }));
    markDirty();
  };

  const togglePreventDuplicate = () => {
    setPreventDuplicateInstalls((prev) => !prev);
    markDirty();
  };

  useEffect(() => {
    setLoading(true);
    trackstar('internal/org')
      .then(({ data }) => {
        setLookbackSettings(data.first_crawl_lookback_settings || {});
        setPreventDuplicateInstalls(data.prevent_duplicate_installs || false);
      })
      .catch(() => setError('Could not load your connection settings.'))
      .finally(() => setLoading(false));
  }, []);

  const saveSettings = () => {
    setSaving(true);
    setError(null);
    trackstar('internal/org-settings', 'POST', {
      // send the full object so types not shown in the form (erp, freight) are preserved
      first_crawl_lookback_settings: lookbackSettings,
      prevent_duplicate_installs: preventDuplicateInstalls,
    })
      .then((res) => {
        if (res && res.error) {
          setError(res.error);
          return;
        }
        setUpdateDisabled(true);
      })
      .catch(() => setError('Could not update your connection settings.'))
      .finally(() => setSaving(false));
  };

  return (
    <div className="flex flex-col gap-5">
      <SettingsHeader
        title="Connections"
        subtitle="Manage connection defaults"
        actions={saving ? (
          <ClipLoader color="#D4532A" size={20} />
        ) : (
          <button
            type="button"
            className={`btn ${(hasLookbackError || updateDisabled) ? 'bg-surface-200 text-surface-400 cursor-not-allowed' : 'btn-accent'}`}
            onClick={saveSettings}
            disabled={hasLookbackError || updateDisabled}
          >
            Update
          </button>
        )}
      />

      {error && <span className="text-sm text-error">{error}</span>}

      <div className="card p-6 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-surface-700">Lookback Days</span>
          <p className="text-sm text-surface-500">
            The number of days that Trackstar will go back and sync data for new connections,
            set per integration type. Must be between 1 and 1095 (365 * 3).
          </p>
          {loading ? <ClipLoader color="#D4532A" size={20} /> : (
            <div className="flex flex-col gap-3 mt-2">
              {TYPES.map(({ key, label }) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-sm text-surface-600 w-20">{label}</span>
                  <input
                    type="number"
                    name={`lookback-${key}`}
                    className={`input w-20 text-center ${isInvalid(lookbackSettings[key]) ? 'border-error focus:border-error focus:ring-error/20' : ''}`}
                    min="1"
                    max="1095"
                    step="30"
                    value={Number.isFinite(lookbackSettings[key]) ? lookbackSettings[key] : ''}
                    onChange={(e) => handleLookbackChange(key, e)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-surface-200" />
        <div className="flex items-center justify-between">
          <div className="flex flex-col pr-6">
            <span className="text-sm font-medium text-surface-700">Prevent Duplicate Connection Installation</span>
            <span className="text-sm text-surface-500 mt-1">
              If a user tries to install a connection that already exists with those credentials,
              overwrite the existing connection instead of creating a new one.
            </span>
          </div>
          {loading ? <ClipLoader color="#D4532A" size={20} /> : (
            <button
              type="button"
              onClick={togglePreventDuplicate}
              aria-label="Toggle Prevent Duplicate Connection Installation"
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${preventDuplicateInstalls ? 'bg-surface-900' : 'bg-surface-300'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${preventDuplicateInstalls ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConnectionSettings;
